import {
 createTheme,
 Shadows,
 Theme,
 ThemeOptions,
} from '@mui/material/styles';
// The import can be in any file that is included in your `tsconfig.json`
import type {} from '@mui/material/themeCssVarsAugmentation';
import type {} from '@mui/x-data-grid-premium/themeAugmentation'; // important
import IconAngleBottom from '@assets/icons/angle-bottom.svg?react';
import { Box } from '@mui/material';
import customColors from './customColors';
import customColorsDark from './customColorsDark';
import IconCheck from '@assets/icons/check.svg?react';
import IconCheckbox from '@assets/icons/checkbox.svg?react';

declare module '@mui/material/styles' {
 interface PaletteOptions {
  kemek: {
   black: string;
   darkestGray: string;
   darkGray: string;
   gray: string;
   lightGray: string;
   lightestGray: string;
   white: string;
   blue: string;
   lightBlue: string;
   green: string;
   lightGreen: string;
   red: string;
   lightRed: string;
   orange: string;
   lightOrange: string;
   yellow: string;
   lightYellow: string;
   bg1: string;
   bg2: string;
  };
 }
 interface Palette {
  kemek: {
   black: string;
   darkestGray: string;
   darkGray: string;
   gray: string;
   lightGray: string;
   lightestGray: string;
   white: string;
   blue: string;
   lightBlue: string;
   green: string;
   lightGreen: string;
   red: string;
   lightRed: string;
   orange: string;
   lightOrange: string;
   yellow: string;
   lightYellow: string;
   bg1: string;
   bg2: string;
  };
 }
}

// create a temporary theme to get the default options
const defaultTheme = createTheme();

const defaultShadows: ThemeOptions['shadows'] = [...defaultTheme.shadows];

const themeOptions = () => {
 let th = createTheme({
  shadows: defaultShadows.map(
   () => '0px 4px 12px 0px rgba(0, 0, 0, 0.08)',
  ) as Shadows,
  colorSchemes: {
   light: {
    palette: {
     kemek: customColors,
     primary: {
      main: customColors.blue,
     },
     secondary: {
      main: customColors.darkGray,
     },
     background: {
      default: customColors.bg2,
      paper: '#ffffff',
     },
     text: {
      primary: customColors.darkestGray,
      secondary: customColors.darkestGray,
      disabled: customColors.gray,
     },
     error: {
      main: customColors.red,
      contrastText: '#ffffff',
     },
     warning: {
      main: '#f3ca36',
      contrastText: '#ffffff',
     },
     info: {
      main: customColors.blue,
      contrastText: '#ffffff',
     },
     success: {
      main: customColors.green,
      contrastText: '#ffffff',
     },

     // amber: {
     //  lev0: amber[50],
     //  lev1: amber[100],
     //  lev2: amber[200],
     //  lev3: amber[300],
     //  lev4: amber[400],
     //  lev5: amber[500],
     //  lev6: amber[600],
     //  lev7: amber[700],
     //  lev8: amber[800],
     //  lev9: amber[900],
     // },
    },
   },
   dark: {
    palette: {
     kemek: customColorsDark,
     primary: {
      main: customColorsDark.blue,
     },
     secondary: {
      main: customColorsDark.darkGray,
     },
     text: {
      primary: customColorsDark.darkestGray,
      secondary: customColorsDark.darkestGray,
      disabled: customColorsDark.gray,
     },
     background: {
      default: customColorsDark.bg2,
      paper: customColorsDark.white,
     },
    },
   },
  },
  defaultColorScheme: 'light',
  typography: {
   fontFamily: 'Poppins',
   fontSize: 12.25, // fix
   button: {
    textTransform: 'none',
   },
  },
  shape: {
   borderRadius: 4,
  },
 });
 th = createTheme(th, {
  cssVariables: true,

  components: {
   MuiDataGrid: {
    styleOverrides: {
     root: {
      border: (theme: Theme) =>
       theme.palette.mode === 'dark' ? 'none' : '#e0e0e0', // this is not in design
     },
     menu: {
      '& .MuiDataGrid-menuList': {
       padding: 0,
       border: `1px solid ${customColors.lightestGray}`,
       borderRadius: 4,
       '& .MuiDivider-root': {
        display: 'none !important',
       },
      },
     },
    },
   },
   MuiInputBase: {
    styleOverrides: {
     root: {},
    },
   },
   MuiOutlinedInput: {
    styleOverrides: {
     root: {
      paddingTop: '12px',
      paddingBottom: '12px',
      '& .MuiOutlinedInput-notchedOutline': {
       borderColor: th.palette.kemek.lightGray, // default
       transition: 'border-color 0.3s ease-in-out',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
       borderColor: th.palette.kemek.gray, // hover
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
       borderColor: th.palette.kemek.darkGray, // focus
      },
      '& .MuiOutlinedInput-root:has(input:not(:placeholder-shown)) .MuiOutlinedInput-notchedOutline':
       {
        borderColor: 'purple',
       },
      '& .MuiOutlinedInput-input.Mui-disabled': {
       color: th.palette.kemek.lightGray,

       '& .MuiOutlinedInput-notchedOutline': {
        borderColor: th.palette.kemek.lightGray,
       },
      },
      '&:not(.MuiInputBase-multiline)': {
       '& .MuiOutlinedInput-input': {
        paddingTop: '0px',
        paddingBottom: '0px',
        height: '24px',
       },
       '&.MuiInputBase-sizeSmall': {
        paddingTop: '8px',
        paddingBottom: '8px',
        '& .MuiOutlinedInput-input': {
         paddingTop: '0px',
         paddingBottom: '0px',
        },
       },
      },
      '&.Mui-disabled': {
       '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: th.palette.kemek.lightGray, // hover
       },
      },
     },
    },
   },
   MuiInputLabel: {
    styleOverrides: {
     outlined: {
      transform: 'translate(14px, 14px) scale(1)',
      '&.MuiInputLabel-shrink': {
       transform: 'translate(14px, -8px) scale(0.75)',
      },
      '&.MuiInputLabel-sizeSmall': {
       transform: 'translate(14px, 10px) scale(1)',
       '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -8px) scale(0.75)',
       },
      },
     },
     filled: {
      transform: 'translate(14px, 17px) scale(1)',
      '&.MuiInputLabel-shrink': {
       transform: 'translate(12px, 3px) scale(0.75)',
      },
      '&.MuiInputLabel-sizeSmall': {
       transform: 'translate(14px, 10px) scale(1)',
       '&.MuiInputLabel-shrink': {
        transform: 'translate(12px, 3px) scale(0.75)',
       },
      },
     },
     root: {
      '&.Mui-focused': {
       color: th.palette.kemek.darkestGray,
      },
     },
    },
   },
   MuiFormControlLabel: {
    styleOverrides: {
     root: {
      marginLeft: 0,
     },
    },
   },

   MuiSelect: {
    defaultProps: {
     IconComponent: () => (
      <Box
       sx={{
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '8px',
        // position: 'absolute',
        // top: '50%',
        // transform: 'translateY(-50%)',
        // right: '4px',
        pointerEvents: 'none',
        color: th.palette.kemek.darkestGray,
       }}
      >
       <IconAngleBottom style={{}} />
      </Box>
     ),
    },
    styleOverrides: {
     select: {
      '&.MuiOutlinedInput-input.MuiInputBase-inputSizeSmall': {
       height: '20px',
      },
     },
    },
   },
   MuiAutocomplete: {
    defaultProps: {
     popupIcon: (
      <Box
       sx={{
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: th.palette.kemek.darkestGray,
        pointerEvents: 'none',
       }}
      >
       <IconAngleBottom style={{}} />
      </Box>
     ),
    },
    styleOverrides: {
     root: {
      '& .MuiOutlinedInput-root': {
       paddingTop: '12px',
       paddingBottom: '12px',
       '& .MuiAutocomplete-input': {
        padding: 0,
        paddingLeft: '6px',
       },
       '&.MuiInputBase-sizeSmall': {
        paddingTop: '8px',
        paddingBottom: '8px',
       },

       '& .MuiAutocomplete-tag': {
        margin: '1px',
        height: '26px',
       },
       '&.Mui-disabled': {
        '& .MuiBox-root': {
         color: th.palette.kemek.lightGray, // angle down svg color
        },
       },
      },
      // '& .MuiOutlinedInput-input': {
      //  height: '24px',
      //  padding: 0,
      // },
      // '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {},
     },
    },
   },
   MuiCssBaseline: {
    styleOverrides: {
     body: {
      '& ::-webkit-scrollbar': {
       width: '6px',
       height: '6px',
      },
      '& ::-webkit-scrollbar-track': {
       background: th.palette.kemek.lightestGray,
      },
      '& ::-webkit-scrollbar-thumb': {
       background: th.palette.kemek.gray,
       borderRadius: '3px',
       scrollbarColor: '#969696 #dddddd',
      },
      // column menu
      '& .MuiDataGrid-menuList': {
       padding: 0,
       border: `1px solid ${th.palette.kemek.lightestGray}`,
       borderRadius: 4,
       '& .MuiDivider-root': {
        display: 'none !important',
       },
      },
     },
    },
   },
   MuiIconButton: {
    styleOverrides: {
     root: {
      padding: '4px',
     },
    },
   },
   MuiButton: {
    styleOverrides: {
     root: {
      fontSize: '12px', // fix
      lineHeight: 1.5,
      '&.MuiButton-outlined': {
       height: '40px',
       '&.MuiButton-colorPrimary': {
        borderColor: th.palette.kemek.blue,
       },
      },
      '&.MuiButton-contained': {
       height: '40px',
      },
      '&.Mui-disabled': {
       '&.MuiButton-outlined': {
        borderColor: th.palette.kemek.lightGray,
        color: th.palette.kemek.lightGray,
       },
       '&.MuiButton-contained': {
        color: th.palette.kemek.white,
        backgroundColor: th.palette.kemek.lightGray,
        // backgroundColor: th.palette.kemek.white,
       },
      },
     },
    },
   },
   MuiCheckbox: {
    defaultProps: {
     icon: (
      <Box
       sx={{
        width: '16px',
        height: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
       }}
      >
       <IconCheckbox style={{ width: '16px', height: '16px' }} />
      </Box>
     ),
     checkedIcon: (
      <Box
       sx={{
        width: '16px',
        height: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        borderRadius: '2px',
        backgroundColor: (theme: Theme) => theme.palette.kemek.blue,
       }}
      >
       <IconCheck
        style={{ color: th.palette.kemek.white, width: '8px', height: '6px' }}
       />
      </Box>
     ),
    },
    styleOverrides: {
     root: {
      color: th.palette.kemek.gray,
     },
    },
   },
   MuiAppBar: {
    styleOverrides: {
     colorPrimary: {
      color: th.palette.kemek.darkestGray,
     },
    },
   },
   MuiDialogContent: {
    styleOverrides: {
     root: {
      padding: '16px 24px 0 24px',
     },
    },
   },
   MuiDialogActions: {
    styleOverrides: {
     root: {
      padding: '24px',
      '&>:not(style)~not(style)': {
       background: '5px solid red',
      },
     },
    },
   },
   MuiDivider: {
    styleOverrides: {
     root: {
      borderColor: th.palette.kemek.lightestGray,
     },
    },
   },
   MuiCard: {
    styleOverrides: {
     root: {
      boxShadow: 'none',
     },
    },
   },
   MuiCardContent: {
    styleOverrides: {
     root: {
      '&:last-child': {
       paddingBottom: '16px',
      },
     },
    },
   },
   MuiMenu: {
    styleOverrides: {
     paper: {
      border: `1px solid ${th.palette.kemek.lightestGray}`,
     },
    },
   },
   MuiMenuItem: {
    styleOverrides: {
     root: {
      fontSize: 12,
      gap: '8px',
      padding: '8px 20px 8px 8px',
      borderBottom: `1px solid ${th.palette.kemek.lightestGray}`,
      '&.Mui-selected': {
       backgroundColor: th.palette.kemek.lightBlue,
      },
      '&:last-child': {
       borderBottom: 'none',
      },
     },
    },
   },
   MuiListItemIcon: {
    styleOverrides: {
     root: {
      minWidth: '24px !important',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      // paddingLeft: '4px !important',
      justifyContent: 'center',
     },
    },
   },
   MuiTabs: {
    styleOverrides: {
     root: {
      overflow: 'visible',
     },
     scroller: {
      overflow: 'visible !important',
     },
    },
   },
   MuiTab: {
    styleOverrides: {
     root: {
      fontWeight: 400,
     },
    },
   },
   //  MuiFilledInput: {
   //   styleOverrides: {
   //    root: ({ theme }: { theme: Theme }) => ({
   //     background: theme.palette.kemek.white,
   //     borderRadius: 4,
   //     border: `1px solid ${theme.palette.kemek.lightGray}`,
   //     '&.Mui-error': {
   //      border: `1px solid ${theme.palette.kemek.red}`,
   //     },
   //     '&:hover': {
   //      background: theme.palette.kemek.white,
   //     },
   //     '&.Mui-focused': {
   //      background: theme.palette.kemek.white,
   //     },
   //     '&:not(.MuiInputBase-multiline)': {
   //      '& .MuiFilledInput-input': {
   //       paddingTop: '0px',
   //       paddingBottom: '0px',
   //      },
   //      '&.MuiInputBase-sizeSmall .MuiFilledInput-input': {
   //       paddingTop: '0px',
   //       paddingBottom: '0px',
   //      },
   //     },
   //    }),
   //   },
   //  },
   MuiTextField: {
    styleOverrides: {
     root: {
      //  background: 'transparent',
     },
    },
   },
   MuiListItem: {
    styleOverrides: {
     root: {
      '& .MuiTouchRipple-root': {
       display: 'none',
      },
     },
    },
   },
   // MuiDialogTitle: {
   //  styleOverrides: {
   //   root: {
   //    fontSize: 18,
   //    fontWeight: 500,
   //   },
   //  },
   // },
  },
 });
 return th;
};
export default themeOptions;

